import React from 'react';
import {shallowEqual} from 'react-redux';

import {
  EMPTY_SEARCH_RESULT_PROPS,
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import {
  MultiAxisDimSelection,
  TABLE_ROW,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import TableAxisCell from 'notebook/tables/core-ui/TableAxisCell';
import TableCell, {TableCellProps} from 'notebook/tables/core-ui/TableCell';
import {
  cellFillProps,
  getTableCellAffixedProps,
  isCellSelected,
} from 'notebook/tables/core-ui/TableCellRenderUtils';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import TableGhostCell from 'notebook/tables/core-ui/TableGhostCell';
import {isRowSelected} from 'notebook/tables/core-ui/TableRowUtils';
import {AxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {computeCellCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import {computeVirtualRowIndex} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {RowModel} from 'notebook/tables/store/models/RowModel';
import TableUtil from 'notebook/tables/TableUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import useHasBeenInView from 'ui-library/hooks/useHasBeenInView';
import {Optional} from 'util/Types';

export interface TableRowProps extends MediocreSearchResultProps {
  i: number;

  renderCellsOnly?: boolean;

  // A TableRow can be rendered partially (a "slice") given a start and an end index, which enables
  // rendering an affixed slice and a scrollable slice. The start and end index are restricted to the
  // normal bounds of a row (excluding leading columns, axis column, and ghost cells).
  // - sliceStart of null indicates including up to the first column
  // - sliceEnd of null indicates including up through all of the ghost cells
  sliceStart: number | null;
  sliceEnd: number | null;
  // Because a partial row does not contain all columns, it is not guaranteed that its rendered height
  // will match that of the full row, so we allow that height to be specified.
  rowHeight: Optional<number>;

  // Props for axis cell
  getCopySelection: TableEditableClass['_getCopySelection'];
  getCopySelectionHTML: TableEditableClass['_getCopySelectionHTML'];
  multiPasteHandler: TableEditableClass['multiPasteHandler'];
  hasLockedCellInSelection: TableEditableClass['hasLockedCellInSelection'];
  lockedRowIndices?: Set<number>;
  showDragHandle?: boolean;

  axisSelection?: MultiAxisDimSelection;

  // Actions
  expandToAndEditCell: (index: TableIndex, selectAll?: boolean) => void;
  storeState: TableEditableClass['saveMultiSelect'];
  moveFromCell: TableEditableClass['moveFromCell'];
  selectCell: TableEditableClass['selectCell'];

  // Event handlers
  axisCellDragOver: TableEditableClass['axisCellDragOverHandler'];
  axisCellDragStart: TableEditableClass['axisCellDragStartHandler'];
  axisCellDragHandleMouseDown: TableEditableClass['axisCellDragHandleMouseDown'];
  cellEnterHandler: TableEditableClass['cellEnterHandler'];
  cellEscapeHandler: TableEditableClass['cellEscapeHandler'];
  cellDragOver: TableEditableClass['cellDragOverHandler'];
  cellDragStart: TableEditableClass['multiEditableMouseDownHandler'];
  cellMouseMoveHandler: TableEditableClass['fillDownMouseMoveHandler'];
  // If present, last cell will render fillDown box
  fillDownSquareMouseDownHandler: TableEditableClass['fillDownSquareMouseDownHandler'] | null;
  fillDownSquareDoubleClickHandler: TableEditableClass['fillDownSquareDoubleClickHandler'] | null;
  keyDownHook: TableEditableClass['hideHoverCell'];

  // Performance Optimizations
  calculateInView?: boolean;
}

function renderTableCell(
  props: TableRowProps,
  rowModel: RowModel,
  j: number,
  hasRowBeenInView?: boolean
): JSX.Element | null {
  const {i} = props;

  const columnConstraint = getColumnConstraintWithRowData(rowModel.columnConstraints[j], rowModel.rowData);

  const isNotFirstColumnInMerge = rowModel.evaluatedRow[j].mergedBox?.minJ !== j;
  if (TableUtil.isNotRendered(i, j, rowModel.evaluatedRow[j]) && isNotFirstColumnInMerge) {
    return null;
  }

  const colSpan = TableUtil.getColSpan(j, rowModel.row[j], rowModel.evaluatedRow[j]);
  const rowSpan = TableUtil.getRowSpan(i, rowModel.row[j], rowModel.evaluatedRow[j]);

  // If this is the first rendered row of a merge cell, then we must use the virtual row index of the first
  // row of the merge. The only merge cell we render must be the top left cell in the merge, or a simulated
  // version of it if the real top left cell is not rendered.
  const mergedBox = rowModel.evaluatedRow[j].mergedBox;
  const virtualRowIndex = computeVirtualRowIndex(i, mergedBox);

  const lastSyncedUpstreamValue = getLastSyncedUpstreamValueWithColumnConstraint(
    rowModel.rowData?.lastSyncedUpstreamValues,
    columnConstraint
  );
  const tableCellAffixedProps = getTableCellAffixedProps({
    sliceStart: props.sliceStart,
    affixedColumnCount: rowModel.table.affixedColumnCount,
    hasAnyFrozenColumns: rowModel.table.hasAnyFrozenColumns,
    firstColumnWidth: rowModel.table.firstColumnWidth,
    columnWidths: rowModel.table.columnWidths,
    showAffixedShadow: rowModel.table.showAffixedShadow,
    j,
    mergeMaxJ: mergedBox?.maxJ,
  });
  return (
    <TableCell
      key={j}
      {...({
        i: virtualRowIndex,
        j,

        cid: computeCellCid(rowModel.table.cid, virtualRowIndex, j),
        lastSyncedUpstreamValue: lastSyncedUpstreamValue ?? undefined,
        rowSpan,
        colSpan,
        isHiddenMergedCell: virtualRowIndex !== i,

        // If we have formula cell coloring and we are actively editing a cell, show the cell color.
        isFirstColumn: rowModel.table.firstColumnWidth === 0 && j === 0,
        storeState: props.storeState,
        selectCell: props.selectCell,
        getCopySelection: props.getCopySelection,
        getCopySelectionHTML: props.getCopySelectionHTML,
        multiPasteHandler: props.multiPasteHandler,
        hasLockedCellInSelection: props.hasLockedCellInSelection,
        fillDownSquareMouseDownHandler:
          rowModel.hasFillDownSquare && j === rowModel.selectedColIndexMax
            ? props.fillDownSquareMouseDownHandler
            : null,
        fillDownSquareDoubleClickHandler:
          props.fillDownSquareDoubleClickHandler && j === rowModel.selectedColIndexMax
            ? props.fillDownSquareDoubleClickHandler
            : null,
        cellDragStart: props.cellDragStart,
        cellDragOver: props.cellDragOver,
        cellMouseMoveHandler: props.cellMouseMoveHandler,

        hasRowBeenInView,
        ...tableCellAffixedProps,
        ...cellFillProps(rowModel, j),
        ...getSearchResultPropsForIndex(props, j),
      } satisfies TableCellProps)}
    />
  );
}

/**
 * A data row in a TableEditable, starting with an Excel row label (1, 2, 3, ...)
 */
function TableRow({calculateInView = false, ...props}: TableRowProps): JSX.Element | null {
  const {hasBeenInView: hasRowBeenInView, ref} = useHasBeenInView(calculateInView);
  const rowModel = useRowModel();
  // Table-level configuration is identical for every row, so it is relayed onto the row model
  // rather than threaded through props by TableEditable._getTRProps.
  const {table} = rowModel;
  // If the row no longer exists in the store (e.g., parent is rendering with stale row count),
  // skip rendering this row. This can happen when the table shrinks but React hasn't yet
  // re-rendered the parent with the updated row count.
  if (!rowModel.evaluatedRow) {
    return null;
  }

  function canDeleteRows(j: number): boolean {
    const {axisSelection, lockedRowIndices} = props;
    if (lockedRowIndices == null) {
      return true;
    }
    const rowSelection = axisSelection?.row;
    if (!rowSelection) {
      return false;
    }
    const [rowSelectionStartIndex, rowSelectionEndIndex] = rowSelection;
    for (let i = rowSelectionStartIndex; i <= rowSelectionEndIndex; i++) {
      if (lockedRowIndices.has(i)) {
        return false;
      }
    }
    return true;
  }

  function renderAxisCell(hasRowBeenInView?: boolean): JSX.Element {
    return (
      <AxisCellModelProvider key="axis" dim={TABLE_ROW} index={props.i}>
        <TableAxisCell
          showExcessLabels={table.showRowExcessLabels}
          shouldDisableInsertDimBefore={false}
          shouldDisableInsertDimAfter={false}
          shouldDisableDeleteDim={!canDeleteRows(props.i)}
          getExtraContextMenuOptions={table.getExtraRowAxisContextMenuOptions}
          selectionRange={rowModel.selectionRange}
          numDims={table.rowCount}
          allowEmpty={table.allowNoRows}
          isTableSelected={table.isEveryRowSelected}
          cellDragStart={props.axisCellDragStart}
          cellDragOver={props.axisCellDragOver}
          showDragHandle={props.showDragHandle}
          dragHandleMouseDown={props.axisCellDragHandleMouseDown}
          isSelected={isRowSelected(rowModel)}
          rowData={rowModel.rowData}
          rowProperties={rowModel?.rowProperties}
          locked={table.isTableLocked}
          isProtected={table.isProtected}
          isLeftAlign={table.isLeftAlignRowHeader}
          isFirstColumn={table.firstColumnWidth ? table.firstColumnWidth > 0 : false}
          hasRowBeenInView={hasRowBeenInView}
          dataTestAttrs={{component: 'TableAxisCell', element: 'row'}}
          isAffixed={true}
          showAffixedShadow={table.showAffixedShadow && 0 === table.affixedColumnCount}
          affixedLeftSpacing={0}
          disallowCustomColumns={false}
          {...EMPTY_SEARCH_RESULT_PROPS}
        />
      </AxisCellModelProvider>
    );
  }

  function renderGhostCell(j: number): JSX.Element {
    const {i} = props;
    return (
      <TableGhostCell
        key={j}
        cid={computeCellCid(table.cid, i, j)}
        i={i}
        j={j}
        selectCell={props.selectCell}
        expandToAndEditCell={props.expandToAndEditCell}
        cellMouseMoveHandler={props.cellMouseMoveHandler}
        isHeaderRow={rowModel.isHeaderRow}
        isSelected={isCellSelected(rowModel, j)}
        {...cellFillProps(rowModel, j)}
      />
    );
  }

  const useSimpleTableVirtualization = !props.renderCellsOnly;
  const tableCells = TableUtil.mapColumns({
    sliceStart: props.sliceStart,
    sliceEnd: props.sliceEnd,

    ghostCellsCount: table.ghostCellsCount,
    normalCellsCount: rowModel.evaluatedRow.length,

    mapLeadingCell: () => renderAxisCell(useSimpleTableVirtualization ? hasRowBeenInView : true),
    mapNormalCell: (j: number) =>
      renderTableCell(props, rowModel, j, useSimpleTableVirtualization ? hasRowBeenInView : true),
    mapGhostCell: (j: number) => renderGhostCell(j),
  });
  if (props.renderCellsOnly) {
    // If we are rendering only the cells, we don't need to render the tr.  This will be rendered with
    // TableTr by the parent independently.
    return <>{tableCells}</>;
  }
  return (
    <tr
      ref={ref}
      className="mediocre-tableEditable-tableRow"
      style={{height: props.rowHeight || undefined}}
      {...getTestProps({component: 'TableRow', element: 'root', index: props.i.toString()})}
    >
      {tableCells}
    </tr>
  );
}

function WrappedTableRow(props: TableRowProps): JSX.Element | null {
  return (
    <RowModelProvider rowIndex={props.i}>
      <TableRow {...props} />
    </RowModelProvider>
  );
}

export default React.memo(
  WrappedTableRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    Object.keys(nextProps).every((key) => {
      // Function props get regenerated on rerenders, and isEqual will always return false for them.
      // It's safe to assume other props will change if we want a function prop change to trigger with a rerender.
      const isPropComponent = typeof prevProps[key] === 'function' && typeof nextProps[key] === 'function';
      return isPropComponent || shallowEqual(prevProps[key], nextProps[key]);
    })
);
